/**
 * Tournament API endpoints for Lobby2.0
 * Migrated from old lobby TournamentService with React Query patterns
 */

import { TOURNAMENTS_API_URLS, TOURNAMENT_JOIN_REQUEST_ERROR_CODES } from '@repo/common';
import type {
  ITournamentSessionData,
  IGetLeaderboardDataProps,
  IJoinTournamentProps,
  IJoinTournamentResponse,
  ITournamentAPIResponse,
  ILeaderboard
} from 'features/campaigns';
import { buildResourceUrls, isLocalAssetsHost } from '../buildResourceUrls';

const { PREFIX, GET_TOURNAMENT, GET_LEADERBOARD, JOIN_TOURNAMENT } = TOURNAMENTS_API_URLS;

const turnObjectToQueryStringParams = <T extends { [key: string]: string | boolean } | ITournamentSessionData>(obj: T): string =>
  Object.keys(obj)
    .filter(key => obj[key as keyof T]) // take only params with values
    .map((key) => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key as keyof T] as string);
    })
    .join('&');

/**
 * Fetches all tournaments for the player.
 *
 * `stripResourcesHost=true` tells the backend that this client knows how to
 * assemble asset URLs itself, so it can return relative `resources.*` paths.
 * Older clients omit the flag and keep getting full URLs (back-compat).
 */
// fallow-ignore-next-line unused-export
export const fetchTournaments = async (
  host: string, 
  assetsHost: string,
  sessionInfo: ITournamentSessionData
): Promise<ITournamentAPIResponse> => {
  // On local dev keep the full CDN URLs the backend returns (template assets
  // aren't served locally); elsewhere strip the host and assemble URLs here.
  const stripResourcesHost = !isLocalAssetsHost(assetsHost);
  const queryString: string = turnObjectToQueryStringParams({ ...sessionInfo, stripResourcesHost });
  const url = `${host}${PREFIX}${GET_TOURNAMENT}?${queryString}`;

  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to fetch tournaments' 
    }));
    throw new Error(errorData.message || `Failed to fetch tournaments: ${response.statusText}`);
  }

  const data = (await response.json()) as ITournamentAPIResponse;
  return {
    ...data,
    tournaments: data.tournaments?.map((tournament) => buildResourceUrls(tournament, assetsHost)),
  };
};

/**
 * Fetches leaderboard data for a specific tournament
 */
export const fetchLeaderboard = async ({
  host,
  tournamentId,
  tournamentSessionInfo
}: IGetLeaderboardDataProps): Promise<ILeaderboard> => {
  let url = `${host}${PREFIX}${tournamentId}${GET_LEADERBOARD}`;
  
  if (tournamentSessionInfo) {
    url += '?' + turnObjectToQueryStringParams(tournamentSessionInfo);
  }
  
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to fetch leaderboard' 
    }));
    throw new Error(errorData.message || `Failed to fetch leaderboard: ${response.statusText}`);
  }

  const data = await response.json();
  return { ...data, tournamentId } as ILeaderboard;
};

/**
 * Joins a tournament
 */
// fallow-ignore-next-line unused-export
export const joinTournament = async ({
  host,
  data
}: IJoinTournamentProps): Promise<IJoinTournamentResponse[]> => {
  const url = `${host}${PREFIX}${JOIN_TOURNAMENT}`;

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ 
      message: 'Failed to join tournament' 
    }));
    throw new Error(errorData.message || `Failed to join tournament: ${response.statusText}`);
  }

  const responseData = await response.json() as IJoinTournamentResponse[];

  // Check for join errors
  responseData.forEach(joinData => {
    if (joinData.errorCode) {
      const errorMessage = TOURNAMENT_JOIN_REQUEST_ERROR_CODES[joinData.errorCode] || 'Unknown error';
      throw new Error(`Tournament join failed with error ${joinData.errorCode}: ${errorMessage}`);
    }
  });

  return responseData;
};
